import { DimensionDetail } from "../../api/models/dimension-detail";
import NotificationManager from "../../common/notification/NotificationManager";
import { formatMessage } from "./LanguageHelper";
import { Scenario } from "../../api/models/scenario";
import {
  CoObjectItem,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkTextSource,
} from "../../components/interfaces/CoObject";
import { validateDimensionsAndParams } from "./ObjectCheckerHelper";
import { fetchReferringScenarios } from "../../api/object/Scenario";
import { CPMVersions, isCpmVersionLess } from "./ApiResolver";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";

const SCENARIO = "SCE";

export const isScenario = (dimension: DimensionDetail) => dimension?.structureCode === SCENARIO;

export const getReferrableScenarios = (inheritableParameters: DimensionDetail[]) => {
  const scenarios = inheritableParameters?.filter((p) => isScenario(p) && p.values.length === 1);
  return scenarios?.length === 1 ? scenarios[0] : null;
};

export const getReferringScenarios = async (inheritableParameters: DimensionDetail[], dbId: string) => {
  const referrableScenario = getReferrableScenarios(inheritableParameters);
  if (!referrableScenario) return null;

  const scenarioOid = referrableScenario.values[0]?.oid;
  return scenarioOid ? fetchReferringScenarios(dbId, scenarioOid) : null;
};

export const notifyRefScenarioErrors = (
  inheritableReferrableScenario: DimensionDetail,
  referringScenario: Scenario
) => {
  // the previously inherited scenario is no longer referable (!=1 values)
  if (!inheritableReferrableScenario) {
    NotificationManager.addNotification({
      key: "unreferable.error",
      title: "",
      message: formatMessage("cof.createnewobject.referringscenario.unreferable.error"),
      type: "error",
      isTemporary: false,
    });
    return;
  }
  if (referringScenario) {
    //if the fresh inheritable scenario is present, but the selected ref is no longer present
    NotificationManager.addNotification({
      key: "unavailable.error",
      title: "",
      message: formatMessage("cof.createnewobject.referringscenario.unavailable.error"),
      type: "error",
      isTemporary: false,
    });
  }
};

export const validateRefScenario = (coObject: CoObjectItem, referringScenario: Scenario | null): boolean => {
  const validate = (dimensionDetail: DimensionDetail) => {
    return (
      !dimensionDetail.reference ||
      referringScenario?.referenceScenarios.some((ref) => ref.reference === dimensionDetail.reference)
    );
  };

  return validateDimensionsAndParams(coObject, validate);
};

export const fetchReferringScenario = async (
  coObject: CoObjectItem,
  documentPartMetadata: DocumentPartMetadata,
  isEdit: boolean = true
) => {
  const source = coObject?.source as any;
  const dbId = source?.dbApplication?.id;
  const inheritableParameters = dbId
    ? documentPartMetadata.parametersMetadata?.find((p) => p.dbId === dbId)?.dimensions
    : undefined;

  if (!isCpmVersionLess(CPMVersions.CPM_VERSION32_0)) {
    try {
      return await getReferringScenarios(inheritableParameters, dbId);
    } catch (e) {
      const dimOrParams = (coObject?.source as TgkAmountObjectSource)?.dimensions || coObject?.parameters;
      const hasReferences = dimOrParams?.some((d) => d?.reference);
      if (e?.status === 409 && isEdit && hasReferences) {
        NotificationManager.addNotification({
          title: "",
          message:
            e.message +
            ". You can save the object, but in doing so you will lose the previously selected referring scenario",
          type: "error",
        });
      }
    }
  }
  return null;
};

export const notifyInvalidReferenceScenario = (
  coObject: CoObjectItem,
  inheritableParameters: DimensionDetail[],
  referringScenarios: Scenario | null
) => {
  switch (coObject?.info?.objectType) {
    case "AMOUNT":
    case "AMOUNT_AIH": {
      const dimensions = (coObject.source as TgkAmountObjectSource)?.dimensions;
      const parameters = coObject?.parameters as DimensionDetail[];
      notifyInvalidReferenceScenarioForDimensions(dimensions, inheritableParameters, referringScenarios);
      notifyInvalidReferenceScenarioForParameters(parameters, inheritableParameters, referringScenarios);
      break;
    }
    case "DIMENSION_ATTRIBUTE": {
      const dimensions = (coObject.source as TgkDimensionAttributeSource)?.dimensions;
      notifyInvalidReferenceScenarioForDimensions(dimensions, inheritableParameters, referringScenarios);
      break;
    }
    case "TABLE":
    case "CHART": {
      const parameters = coObject.parameters as DimensionDetail[];
      notifyInvalidReferenceScenarioForParameters(parameters, inheritableParameters, referringScenarios);
      break;
    }
    case "TGK_TEXT": {
      const dimensions = (coObject.source as TgkTextSource)?.dimensions;
      notifyInvalidReferenceScenarioForDimensions(dimensions, inheritableParameters, referringScenarios);
      break;
    }
    default:
      console.log(`For object with type ${coObject?.info?.objectType} we don't check reference scenario validity`);
  }
};

const notifyInvalidReferenceScenarioForDimensions = (
  dimensions: DimensionDetail[],
  inheritableParameters: DimensionDetail[],
  referringScenarios?: Scenario
) => {
  dimensions
    .filter((d) => d.inherited && d.reference)
    .forEach((dim) => {
      const hasInheritableMatch = inheritableParameters?.some((p) => p.dimensionOid === dim.dimensionOid);
      if (hasInheritableMatch) {
        checkAndNotifyInvalidReferenceScenario(dim.reference, inheritableParameters, referringScenarios);
      }
    });
};

const notifyInvalidReferenceScenarioForParameters = (
  parameters: DimensionDetail[],
  inheritableParameters: DimensionDetail[],
  referringScenarios?: Scenario
) => {
  parameters
    .filter((p) => p.inherited && p.reference)
    .forEach((p) => {
      const hasInheritableMatch = inheritableParameters?.some((ip) => ip.structureCode === p.structureCode);
      if (hasInheritableMatch) {
        checkAndNotifyInvalidReferenceScenario(p.reference, inheritableParameters, referringScenarios);
      }
    });
};

const checkAndNotifyInvalidReferenceScenario = (
  reference: string,
  inheritableParameters: DimensionDetail[],
  referringScenario: Scenario | null
) => {
  const inheritableReferrableScenario = getReferrableScenarios(inheritableParameters);
  const isRefStillPresent = referringScenario?.referenceScenarios.some((ref) => ref.reference === reference);

  if (!inheritableReferrableScenario || !isRefStillPresent) {
    notifyRefScenarioErrors(inheritableReferrableScenario, referringScenario);
  }
};
